// src/lib/blog.ts
//
// Blog utilities for local MDX-based blog posts.
// Uses Vite virtual module for pre-extracted metadata (no dynamic imports on list page)
// and import.meta.glob for lazy single-post loading.

import type { ComponentType } from "react";
import manifest from "virtual:blog-manifest";

export interface BlogMeta {
  title: string;
  slug: string;
  excerpt: string;
  publishedAt: string;
  tags: string[];
  voice: string;
  author: string;
  seoTitle: string;
  seoDescription: string;
}

type BlogModule = {
  default: ComponentType;
  meta: BlogMeta;
};

// Auto-discover all blog MDX files via Vite glob import (lazy by default)
const blogModules = import.meta.glob<BlogModule>(
  "../blog-content/*.mdx"
);

/**
 * Get all blog post metadata, sorted by publishedAt descending.
 * Uses the pre-built virtual module manifest — no dynamic imports needed.
 */
export async function getAllPosts(): Promise<BlogMeta[]> {
  return manifest;
}

/**
 * Get a single blog post's metadata by slug.
 */
export async function getPostMeta(slug: string): Promise<BlogMeta | null> {
  const posts = await getAllPosts();
  return posts.find((p) => p.slug === slug) ?? null;
}

/**
 * Get a blog post's component and metadata by slug.
 * Dynamically imports only the requested MDX file.
 */
export async function getPost(slug: string): Promise<BlogModule | null> {
  const key = Object.keys(blogModules).find((k) =>
    k.endsWith(`/${slug}.mdx`)
  );
  if (!key) return null;

  try {
    return await blogModules[key]();
  } catch {
    return null;
  }
}

/**
 * Get the hero image path for a blog post by slug.
 * Returns the local path (served from public/blog/heroes/).
 */
export function getHeroImage(slug: string): string {
  return `/blog/heroes/${slug}.png`;
}

/**
 * Get all unique tags across all posts.
 */
export async function getAllTags(): Promise<string[]> {
  const posts = await getAllPosts();
  const tagSet = new Set<string>();
  for (const post of posts) {
    for (const tag of post.tags) {
      tagSet.add(tag);
    }
  }
  return Array.from(tagSet).sort();
}
